import {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  ChatGroup,
  ChatScreenState,
  ChatScreenType,
  ChatUser,
  Company,
  CompanyGroup,
} from "../types/chat";
import { useNavigate, useSearchParams } from "react-router-dom";
import webSocketHandler from "@/composables/webSocketHandler";
import { CreateGroupResponse } from "../types/api-response-types";
import actionsHandler from "@/store/actions/actionHandler";
import { useDispatch, useSelector } from "react-redux";
import { AppThunkDispatch, RootState } from "@/store";

const LiveChatStateContext = createContext<{
  currentScreen: ChatScreenState | null;
  setCurrentScreen: (screen: ChatScreenState) => void;
  handleScreenChange: (screen: ChatScreenState) => void;
  userInfo: ChatUser | null;
  companyInfo: Company | null;
  companyGroups: CompanyGroup[];
  chatGroups: ChatGroup[];
  generalGroup: ChatGroup | null;
  setUserInfo: (userInfo: ChatUser | null) => void;
  setCompanyInfo: (companyInfo: Company | null) => void;
  setCompanyGroups: (
    companyGroups: CompanyGroup[] | ((prev: CompanyGroup[]) => CompanyGroup[]),
  ) => void;
  setChatGroups: (
    chatGroups: ChatGroup[] | ((prev: ChatGroup[]) => ChatGroup[]),
  ) => void;
  setGeneralGroup: (generalGroup: ChatGroup | null) => void;
  unreadCounts: UnreadCounts;
  setUnreadCounts: React.Dispatch<React.SetStateAction<UnreadCounts>>;
  incrementUnreadCount: (roomId: string) => void;
  resetUnreadCount: (roomId: string) => void;
  isIndividualChatCreationLoading: {
    [key: string]: boolean;
  };
  setIsIndividualChatCreationLoading: React.Dispatch<
    React.SetStateAction<{
      [key: string]: boolean;
    }>
  >;
  handleCreatingIndividualChatRoom: (
    user: ChatUser,
  ) => Promise<CreateGroupResponse>;
  documentChatRoomIds: string[];
  setDocumentChatRoomIds: React.Dispatch<React.SetStateAction<string[]>>;
}>({
  currentScreen: null,
  setCurrentScreen: () => {},
  handleScreenChange: () => {},
  userInfo: null,
  companyInfo: null,
  companyGroups: [],
  chatGroups: [],
  generalGroup: null,
  setUserInfo: () => {},
  setCompanyInfo: () => {},
  setCompanyGroups: () => {},
  setChatGroups: () => {},
  setGeneralGroup: () => {},
  unreadCounts: {},
  setUnreadCounts: () => {},
  incrementUnreadCount: () => {},
  resetUnreadCount: () => {},
  isIndividualChatCreationLoading: {},
  setIsIndividualChatCreationLoading: () => {},
  handleCreatingIndividualChatRoom: () =>
    Promise.resolve({} as CreateGroupResponse),
  documentChatRoomIds: [],
  setDocumentChatRoomIds: () => {},
});

export const useLiveChatState = () => {
  return useContext(LiveChatStateContext);
};

interface UnreadCounts {
  [key: string]: number; // roomId: count
}

const LiveChatStateProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentScreen, setCurrentScreen] = useState<ChatScreenState | null>(
    null,
  );
  const [companyGroups, setCompanyGroups] = useState<CompanyGroup[]>([]);
  const [chatGroups, setChatGroups] = useState<ChatGroup[]>([]);
  const [userInfo, setUserInfo] = useState<ChatUser | null>(null);
  const [companyInfo, setCompanyInfo] = useState<Company | null>(null);
  const [generalGroup, setGeneralGroup] = useState<ChatGroup | null>(null);
  const [unreadCounts, setUnreadCounts] = useState<UnreadCounts>({});
  const [isIndividualChatCreationLoading, setIsIndividualChatCreationLoading] =
    useState<{
      [key: string]: boolean;
    }>({});
  const { handleListening, closeAllSockets } = webSocketHandler();
  const dispatch = useDispatch<AppThunkDispatch>();

  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const [listeningRooms, setListeningRooms] = useState<(() => void)[]>([]);
  const [documentChatRoomIds, setDocumentChatRoomIds] = useState<string[]>([]);

  const user = useSelector((state: RootState) => state.auth.user);
  /**
   * This effect is used to set the current screen based on the query params
   */
  useEffect(() => {
    const screen = queryParams.get("screen");
    const id = queryParams.get("id");
    const companyId = queryParams.get("company_id");

    if (screen) {
      setCurrentScreen({
        type: screen as ChatScreenType,
        data: {
          user:
            screen === ChatScreenType.USER
              ? { id: Number(id), name: "", room_id: "" }
              : undefined,
          companyId: companyId ?? undefined,
          group:
            screen === ChatScreenType.GROUP
              ? { room_id: Number(id), name: "", users: [] }
              : undefined,
        },
      });
    }
  }, [queryParams]);

  /**
   * This effect is used to set the current screen based on the company groups
   */
  useEffect(() => {
    if (companyGroups.length > 0 && !currentScreen) {
      const firstUser = companyGroups[0]?.companies[0]?.users[0];
      const companyId = companyGroups[0]?.companies[0]?.company_id;
      if (firstUser) {
        handleScreenChange({
          type: ChatScreenType.USER,
          data: {
            user: firstUser,
            companyId: companyId,
          },
        });
      }
    }
    companyGroups.forEach((group) => {
      group.companies.forEach((company) => {
        company.users.forEach((user) => {
          console.log("user: ", user);
          if (user.room_id && user?.unread_count && user?.unread_count > 0) {
            setUnreadCounts((prev) => ({
              ...prev,
              [user.room_id ?? ""]: user?.unread_count ?? 0,
            }));
          }
        });
      });
    });
    chatGroups.forEach((group) => {
      if (group.room_id && group?.unread_count && group?.unread_count > 0) {
        setUnreadCounts((prev) => ({
          ...prev,
          [group.room_id ?? ""]: group?.unread_count ?? 0,
        }));
      }
    });
  }, [companyGroups, chatGroups]);

  const handleScreenChange = (screenState: ChatScreenState) => {
    const currentPathName = window.location.pathname;
    const id = screenState.data.user?.id ?? screenState.data.group?.room_id;
    const companyId = screenState.data.companyId
      ? `&company_id=${screenState.data.companyId}`
      : "";
    navigate(
      `${currentPathName}?screen=${screenState.type}&id=${id}${companyId}`,
    );
  };

  const incrementUnreadCount = (roomId: string) => {
    setUnreadCounts((prev) => ({
      ...prev,
      [roomId]: (prev[roomId] || 0) + 1,
    }));
  };

  const resetUnreadCount = (roomId: string) => {
    setUnreadCounts((prev) => ({
      ...prev,
      [roomId]: 0,
    }));
  };

  const handleSettingRoomId = (
    response: CreateGroupResponse,
    user: ChatUser,
  ) => {
    setCompanyGroups((prev) => {
      const updatedGroups = prev.map((group) => {
        return {
          ...group,
          companies: group.companies.map((company) => {
            return {
              ...company,
              users: company.users.map((userData) => {
                return {
                  ...userData,
                  room_id:
                    userData.id === user.id
                      ? response.room_id.toString()
                      : userData.room_id,
                };
              }),
            };
          }),
        };
      });
      return updatedGroups;
    });
  };

  const handleCreatingIndividualChatRoom = (
    user: ChatUser,
  ): Promise<CreateGroupResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        setIsIndividualChatCreationLoading((prev) => ({
          ...prev,
          [user.id]: true,
        }));
        const { authenticatedCall } = actionsHandler("createIndividualChat");
        const response = await dispatch(
          authenticatedCall({
            method: "post",
            payload: {
              recipient_id: user.id,
            },
          }),
        );

        if (response.ok) {
          handleSettingRoomId(response.data, user);
          resetUnreadCount(response.data.room_id.toString());
          resolve(response.data as CreateGroupResponse);
        }
      } catch (error) {
        console.error("Error creating individual chat:", error);
        reject(error);
      } finally {
        setIsIndividualChatCreationLoading((prev) => ({
          ...prev,
          [user.id]: false,
        }));
      }
    });
  };

  const listenToRoom = useCallback(
    async (roomId: string, name?: string) => {
      handleListening({ roomId }, (data) => {
        console.log(name + " data: ", data);
        // Only increment if message is not from current user
        if (parseInt(data.sender) !== user?.id) {
          // Only increment if not currently viewing this room
          if (
            currentScreen?.data.group?.room_id.toString() !== roomId &&
            currentScreen?.data.user?.room_id !== roomId
          ) {
            incrementUnreadCount(roomId);
          }
        }
      }).then((res) => {
        const close: (() => void) | undefined = res?.close;
        if (close) {
          setListeningRooms((prev) => [...prev, close]);
        }
      });
    },
    [currentScreen, user],
  );

  const handleListeningToIndividualRooms = useCallback(() => {
    // Listen to all individual chats
    companyGroups.forEach((group) => {
      group.companies.forEach((company) => {
        company.users.forEach((userData) => {
          if (userData.room_id) {
            listenToRoom(userData.room_id);
          }
        });
      });
    });
  }, [chatGroups, companyGroups, listenToRoom]);

  const handleListeningToGroupChats = useCallback(() => {
    // Listen to all group chats
    chatGroups.forEach((group) => {
      listenToRoom(group.room_id.toString());
    });
  }, [chatGroups, companyGroups, listenToRoom]);

  const handleListeningToGeneralChat = useCallback(
    (name?: string) => {
      // Listen to all group chats
      listenToRoom(generalGroup?.room_id.toString() ?? "", name);
    },
    [generalGroup, listenToRoom],
  );

  const handleListeningToDocumentChats = useCallback(() => {
    // Listen to all document chats
    console.log("documentChatRoomIds: ", documentChatRoomIds);
    documentChatRoomIds.forEach((roomId) => {
      listenToRoom(roomId, "document");
    });
  }, [documentChatRoomIds, listenToRoom]);

  // Listen for messages on all rooms
  useEffect(() => {
    handleListeningToIndividualRooms();

    return () => {
      listeningRooms.forEach((close) => close());
    };
  }, [companyGroups, handleListeningToIndividualRooms]);

  useEffect(() => {
    handleListeningToGroupChats();

    return () => {
      listeningRooms.forEach((close) => close());
    };
  }, [chatGroups, handleListeningToGroupChats]);

  useEffect(() => {
    handleListeningToGeneralChat("general");

    return () => {
      listeningRooms.forEach((close) => close());
    };
  }, [generalGroup, handleListeningToGeneralChat]);

  useEffect(() => {
    handleListeningToDocumentChats();

    return () => {
      listeningRooms.forEach((close) => close());
    };
  }, [documentChatRoomIds, handleListeningToDocumentChats]);

  const providerValue = useMemo(
    () => ({
      currentScreen,
      setCurrentScreen,
      handleScreenChange,
      userInfo,
      companyInfo,
      setUserInfo,
      setCompanyInfo,
      companyGroups,
      chatGroups,
      generalGroup,
      setCompanyGroups,
      setChatGroups,
      setGeneralGroup,
      unreadCounts,
      setUnreadCounts,
      incrementUnreadCount,
      resetUnreadCount,
      isIndividualChatCreationLoading,
      setIsIndividualChatCreationLoading,
      handleCreatingIndividualChatRoom,
      documentChatRoomIds,
      setDocumentChatRoomIds,
    }),
    [
      currentScreen,
      setCurrentScreen,
      handleScreenChange,
      userInfo,
      companyInfo,
      setUserInfo,
      setCompanyInfo,
      companyGroups,
      chatGroups,
      generalGroup,
      setCompanyGroups,
      setChatGroups,
      setGeneralGroup,
      unreadCounts,
      setUnreadCounts,
      incrementUnreadCount,
      resetUnreadCount,
      isIndividualChatCreationLoading,
      setIsIndividualChatCreationLoading,
      handleCreatingIndividualChatRoom,
      documentChatRoomIds,
      setDocumentChatRoomIds,
    ],
  );

  return (
    <LiveChatStateContext.Provider value={providerValue}>
      {children}
    </LiveChatStateContext.Provider>
  );
};

export default LiveChatStateProvider;
